import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { SubstitutionRuleActionsMenu } from 'components/fbm/substitutions/SubstitutionRuleActionsMenu';
import { SubstitutionRuleEnabledStatusCell } from 'components/fbm/substitutions/SubstitutionRuleEnabledStatusCell';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

const renderEnabledStatusCell = ({ getValue }) => <SubstitutionRuleEnabledStatusCell isEnabled={getValue()} />;

const renderSubstitutesCell = ({ getValue }) => {
    const substitutes = getValue();

    if (!Array.isArray(substitutes) || substitutes.length === 0) {
        return <span className="text-muted-foreground">—</span>;
    }

    return (
        <ul className="flex flex-col gap-2">
            {substitutes.map((substitute) => (
                <li
                    key={`${substitute?.substituteExternalSku ?? 'sku'}-${substitute?.substituteName ?? 'name'}`}
                    className="rounded-md border border-border bg-muted/30 p-2"
                >
                    <div className="text-sm font-medium">{substitute?.substituteName ?? '—'}</div>
                    <div className="text-xs text-muted-foreground">
                        SKU: {substitute?.substituteExternalSku ?? '—'} · Qty: {substitute?.substituteQuantity ?? '—'}
                    </div>
                </li>
            ))}
        </ul>
    );
};

const renderActionsCell = ({ row, table }) => {
    const onToggleEnabled = table.options.meta?.onToggleEnabled;
    const onDelete = table.options.meta?.onDelete;
    const facilityName = table.options.meta?.facilityName;
    const rule = row.original;
    const isEnabled = rule?.isEnabled !== false;

    return (
        <SubstitutionRuleActionsMenu
            facilityName={facilityName}
            isEnabled={isEnabled}
            onToggleEnabled={() => onToggleEnabled?.(rule)}
            onDelete={() => onDelete?.(rule)}
        />
    );
};

const MemoizedSubstitutionRuleRow = React.memo(({ row }) => (
    <TableRow>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

MemoizedSubstitutionRuleRow.displayName = 'MemoizedSubstitutionRuleRow';

/**
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.data=[]]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isFetching=false]
 * @param {string} [props.emptyMessage='No substitution rules found.']
 * @param {string} [props.facilityName]
 * @param {(rule: Record<string, unknown>) => void} [props.onToggleEnabled]
 * @param {(rule: Record<string, unknown>) => void} [props.onDelete]
 */
export function SubstitutionRulesTable({
    data = [],
    emptyMessage = 'No substitution rules found.',
    facilityName,
    isFetching = false,
    isLoading = false,
    onDelete,
    onToggleEnabled,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'ruleGroupId',
                header: 'Rule Group ID',
            },
            {
                accessorKey: 'isEnabled',
                cell: renderEnabledStatusCell,
                header: 'Enabled',
            },
            {
                accessorKey: 'originalExternalSku',
                header: 'Original External SKU',
            },
            {
                accessorKey: 'originalName',
                header: 'Original Name',
            },
            {
                accessorKey: 'substitutionRuleQuantitySet',
                cell: renderSubstitutesCell,
                header: 'Substitutes',
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: renderActionsCell,
            },
        ],
        [],
    );

    const tableMeta = useMemo(
        () => ({
            facilityName,
            onDelete,
            onToggleEnabled,
        }),
        [facilityName, onDelete, onToggleEnabled],
    );

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) => (row?.id != null ? String(row.id) : String(index)),
        meta: tableMeta,
    });

    return (
        <div className="flex flex-1 flex-col">
            {isFetching && !isLoading ? (
                <div className="mb-2 h-1 w-full overflow-hidden rounded bg-muted">
                    <div className="h-full w-1/3 animate-pulse bg-primary/40" />
                </div>
            ) : null}
            <div className="flex min-h-[240px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading substitution rules…</span>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-md border pb-2">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead
                                                key={header.id}
                                                className="whitespace-normal break-words text-left"
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => <MemoizedSubstitutionRuleRow key={row.id} row={row} />)
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}

SubstitutionRulesTable.displayName = 'SubstitutionRulesTable';
